import { z } from 'zod';
/**
 * The vocabulary every other schema is built from. Defined once here so a booking, a
 * payout and a class enrolment all agree on what an id, a date and an amount of money
 * look like — and so changing that agreement is one edit.
 */
/** UUIDv7 in practice, but validated as a uuid so fixtures stay easy to write. */
export const Id = z.string().uuid();
/**
 * Money is integer minor units, never a float. Architecture plan §13.1: no floating
 * point anywhere in the money path. Always paired with a currency at the boundary.
 */
export const Cents = z.number().int();
export const Currency = z.literal('CAD');
export const Money = z.object({ amount_cents: Cents, currency: Currency });
/** A calendar day in the account's own timezone — "Tuesday's water" must not move. */
export const CalendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
/** An instant. Always UTC on the wire; the client renders it in local time. */
export const Instant = z.string().datetime({ offset: true });
/**
 * An IANA time zone — "America/Toronto", never an offset. Checked against the zones
 * the server's own Intl knows, so a typo is refused rather than stored and then read
 * back on the wrong clock by every screen.
 */
export const TimeZone = z
    .string()
    .min(1)
    .max(64)
    .refine((zone) => {
    try {
        new Intl.DateTimeFormat('en-CA', { timeZone: zone });
        return zone.includes('/') || zone === 'UTC';
    }
    catch {
        return false;
    }
}, { message: 'Not a time zone this platform knows, such as America/Toronto.' });
export const Email = z.string().email().max(320).toLowerCase().trim();
export const PhoneE164 = z.string().regex(/^\+[1-9]\d{7,14}$/, 'Expected E.164, e.g. +16045550134');
/**
 * Password rule shown to the user *before* they type (flows 1.3), so it lives here
 * rather than in a handler — the sign-up screen and the reset screen must not drift.
 */
export const Password = z
    .string()
    .min(8, 'At least eight characters, with one number.')
    .max(200)
    .regex(/\d/, 'At least eight characters, with one number.');
export const OtpCode = z.string().regex(/^\d{6}$/, 'Six digits');
/** Public references, e.g. "HI 4537" — what the person screenshots. */
export const PublicReference = z.string().regex(/^HI \d{4,}$/);
export const IanaTimezone = z.string().min(3).max(64);
export const CityId = Id;
/**
 * The four the whole product is organised by — Health & Mind, Money & Tax, Business &
 * Legal and Mentors on the marketplace, and All / Health / Money / Business /
 * Mentors on the Learn tab. The enum value stays `mentorship`; the word on screen
 * is **Mentors**, and it comes from `PILLAR_LABELS` rather than from the key.
 *
 * One vocabulary rather than two. A product that calls them pillars in one section and
 * topics in another is a product where a filter quietly stops matching. **This is the
 * only place the list is written.** It was written in three places until 2026-09-21,
 * and adding the fourth found that two of them were copies — which is the whole
 * argument for this being a shared primitive.
 */
export const Pillar = z.enum(['mind_body', 'personal_money', 'business', 'mentorship']);
/**
 * Where an appointment happens. Decision 2.6 (client, 2026-09-25): each service is
 * one of these or both; a member is asked only when it is both, and the booking
 * records the answer. The one place the two are listed — the database's checks
 * (manual/after/0026, 0047) hold the same pair.
 */
export const SESSION_MODES = ['in_person', 'video'];
export const SessionMode = z.enum(SESSION_MODES);
/** A service's modes: one or both, never none. */
export const ServiceSessionModes = z.array(SessionMode).min(1).max(2);
/**
 * Decision 5.42 (client, 2026-09-28): how a paid appointment is charged. `full` — the
 * whole price and the booking fee when it is booked (the default, 5.40); `deposit` —
 * her deposit and the booking fee when it is booked, and the rest settled with her on
 * the day. Set per professional, with a per-service choice over it.
 */
export const PAYMENT_MODES = ['full', 'deposit'];
export const PaymentMode = z.enum(PAYMENT_MODES);
//# sourceMappingURL=primitives.js.map